import { useState, type MutableRefObject } from 'react';
import { ListFilter } from 'lucide-react';
import { Button, Popover, PopoverContent, PopoverTrigger } from '@predoc/cow';
import { useListProfiles } from '@/feature/profiles/hooks/profilesAPI';
import type { ProfileFilterState } from '../../types';
import { getHasActiveProfileFilter } from './profileFilter';
import { ProfileFilterList } from './ProfileFilterList';

interface ProfileFilterDropdownProps {
  profileFilter: ProfileFilterState;
  onProfileFilterChange: (profileFilter: ProfileFilterState) => void;
  openStateRef?: MutableRefObject<boolean>;
}

/**
 * Patients-list / org-settings profile filter trigger.
 * Owns the ListFilter button + active indicator; reuses ProfileFilterList for
 * the searchable checkbox content (same list as efax search).
 */
export const ProfileFilterDropdown = ({
  profileFilter,
  onProfileFilterChange,
  openStateRef,
}: ProfileFilterDropdownProps) => {
  // Only used for the active-filter indicator and the "showing first N" hint.
  const { data: totalData } = useListProfiles({
    assignedProfilesOnly: true,
    pagination: { offset: 0, limit: 1 },
  });
  const totalProfileCount = totalData?.totalCount ?? 0;

  const hasActiveFilters = getHasActiveProfileFilter(
    profileFilter,
    totalProfileCount,
  );

  const [open, setLocalOpen] = useState(() => openStateRef?.current ?? false);
  const setOpen = (nextOpen: boolean) => {
    if (openStateRef) openStateRef.current = nextOpen;
    setLocalOpen(nextOpen);
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen: boolean) => {
        // Radix can emit a close event while the table header is being
        // recreated. The actual outside interaction below handles closing.
        if (nextOpen) setOpen(true);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="text-muted-foreground relative h-6 w-6 p-0"
          aria-label="Filter profiles"
        >
          <ListFilter className="size-3" />
          {hasActiveFilters ? (
            <span className="bg-destructive absolute top-0 right-0 h-2 w-2 rounded-full" />
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        forceMount
        className="flex w-80 !animate-none flex-col p-0"
        align="end"
        onPointerDownOutside={() => setOpen(false)}
        onEscapeKeyDown={() => setOpen(false)}
      >
        {open ? (
          <ProfileFilterList
            profileFilter={profileFilter}
            onProfileFilterChange={onProfileFilterChange}
            totalProfileCount={totalProfileCount}
            listClassName="max-h-[400px]"
          />
        ) : null}
      </PopoverContent>
    </Popover>
  );
};
